import React, { useState, useEffect, useContext, useRef } from "react";
import { AudioContext } from "./AudioPlayer";
import { useUser } from "./User";
import { useTheme } from "./ThemeContext"; // Import the theme hook
import config from "./config";
import AudiobookPromptSelector from "./AudiobookPromptSelector";

// Define styles as a function that takes the current theme
const getStyles = (theme) => {
   const isLight = theme === "light";
   return {
      bookCardContainer: {
         display: "flex",
         flexWrap: "wrap",
         margin: "-0.5rem",
         width: "100%",
         justifyContent: "center", // Center the cards
      },
      bookCard: {
         margin: "1rem",
         width: "12.25rem", // Fixed width
         height: "22rem",
         borderRadius: "1.75rem",
         overflow: "hidden",
         border: `1px solid ${
            isLight ? "rgba(229, 229, 229, 1)" : "rgba(229, 229, 229, 0.2)"
         }`,
         cursor: "pointer",
         backgroundColor: isLight
            ? "rgba(255, 255, 255, 0.8)"
            : "rgba(255, 255, 255, 0.1)",
         boxShadow: "0px 4px 4px 0px #00000040",
         flexShrink: 0, // Prevent cards from shrinking in horizontal scroll
         transition: "all 0.2s ease", // Smooth transition for hover effects
      },
      bookCardHover: {
         boxShadow: "0px 8px 8px 0px #00000060",
         border: "2px solid rgba(25, 73, 255, 0.7)",
      },
      bookCardTitle: {
         fontSize: "0.8rem",
         fontWeight: "700",
         marginBottom: "0.5rem",
         color: isLight ? "var(--text-primary)" : "#fff",
      },
      bookCardImage: {
         height: "11.75rem",
         overflow: "hidden",
         position: "relative",
         margin: "5px",
         borderRadius: "1.5rem",
         border: `1px solid ${
            isLight ? "rgba(200, 200, 200, 1)" : "rgba(96, 96, 96, 1)"
         }`,
         boxshadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
      },
      bookCardTag: {
         display: "inline-block",
         padding: "0.25rem 0.5rem",
         marginRight: "0.375rem",
         marginBottom: "0.25rem",
         borderRadius: "0.5rem",
         fontSize: "0.5rem",
         backgroundColor: isLight ? "#EFEFEF" : "#333",
         color: isLight ? "#000" : "#fff",
         fontWeight: "700",
         border: isLight ? "1px solid #e5e7eb" : "1px solid #555",
         boxShadow: isLight
            ? "0px 1px 2px rgba(0, 0, 0, 0.05)"
            : "0px 1px 2px rgba(0, 0, 0, 0.2)",
      },
      bookCardHighlight: {
         fontSize: "0.625rem",
         color: isLight ? "var(--text-primary)" : "#fff",
         marginBottom: "-0.25rem",
         maxHeight: "2rem",
         overflow: "hidden",
      },
      bookCardSeeMore: {
         color: isLight ? "var(--accent-secondary)" : "#4285F4",
         textDecoration: "none",
         fontSize: "0.625rem",
         fontWeight: "700",
      },
      fullWidthContainer: {
         width: "100vw",
         maxWidth: "100vw",
         padding: 0,
         margin: 0,
         marginLeft: "calc(-50vw + 50%)",
         marginRight: "calc(-50vw + 50%)",
         boxShadow: "none",
         borderRadius: 0,
      },
      categorySection: {
         marginBottom: "2.5rem",
         width: "100vw",
         maxWidth: "100vw",
         padding: 0,
         margin: 0,
         marginLeft: "calc(-50vw + 50%)",
         marginRight: "calc(-50vw + 50%)",
         paddingBottom: "20px",
      },
      categoryTitle: {
         fontSize: "1.25rem",
         fontWeight: "600",
         marginBottom: "1rem",
         color: "var(--text-primary)",
         paddingLeft: "0.5rem",
         maxWidth: "1200px",
         margin: "0 auto",
         paddingTop: "20px",
         paddingBottom: "10px",
      },
      categoryBooks: {
         display: "flex",
         flexWrap: "nowrap", // Important - no wrapping
         overflowX: "hidden", // Hide horizontal overflow
         scrollBehavior: "smooth", // Smooth scrolling
         position: "relative",
         maxWidth: "1200px",
         margin: "0 auto",
         padding: "0",
      },
      scrollArrow: {
         position: "absolute",
         top: "50%",
         transform: "translateY(-50%)",
         width: "40px",
         height: "40px",
         borderRadius: "50%",
         backgroundColor: isLight
            ? "rgba(0, 0, 0, 0.1)"
            : "rgba(255, 255, 255, 0.2)",
         color: isLight ? "#333" : "white",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         cursor: "pointer",
         zIndex: 5,
         border: "none",
         fontSize: "20px",
         transition: "background-color 0.2s ease",
         boxShadow: isLight
            ? "0 2px 5px rgba(0, 0, 0, 0.1)"
            : "0 2px 5px rgba(255, 255, 255, 0.1)",
      },
      leftArrow: {
         left: "0",
      },
      rightArrow: {
         right: "0",
      },
   };
};

// Main PublicFeed component with category-based display
export function PublicFeed() {
   const { theme } = useTheme(); // Get current theme
   const styles = getStyles(theme); // Generate styles based on current theme
   const isLight = theme === "light";

   const [publicItems, setPublicItems] = useState([]);
   const [categories, setCategories] = useState({});
   const [allUuids, setAllUuids] = useState([]);
   const [loading, setLoading] = useState(true);
   const [initialLoading, setInitialLoading] = useState(true);
   const [selectedBook, setSelectedBook] = useState(null);
   const { isAuthenticated } = useUser();
   const userId = localStorage.getItem("userId") || "";
   const audioContext = useContext(AudioContext);

   // Create refs for category scroll containers
   const scrollContainerRefs = useRef({});

   // Fetch metadata for a book from CDS
   const fetchBookMetadata = async (uuid) => {
      try {
         console.log(`Fetching metadata for public book ${uuid}`);
         const url = `${config.cdsUrl}/user/audiobooks/${uuid}/metadata?user_id=${userId}`;

         const headers = {
            "Content-Type": "application/json",
            Accept: "application/json",
         };

         const res = await fetch(url, {
            method: "GET",
            headers,
            mode: "cors",
         });

         if (!res.ok) {
            throw new Error(`Error fetching metadata: ${res.status}`);
         }

         const metadata = await res.json();
         console.log(`Metadata received for ${uuid}:`, metadata);

         return {
            ...metadata,
            uuid, // Ensure UUID is included
         };
      } catch (error) {
         console.error("Error fetching metadata for book", uuid, error);
         return {
            uuid,
         };
      }
   };

   // Fetch all audiobooks organized by categories
   const fetchAudiobooks = async () => {
      try {
         setInitialLoading(true);
         const res = await fetch("/api/public/audiobooks");

         if (!res.ok) {
            throw new Error(`Failed to fetch: ${res.status}`);
         }

         const data = await res.json();

         console.log("Received audiobooks by category:", data);

         // Process the new data format
         if (typeof data === "object" && !Array.isArray(data)) {
            const processedCategories = {};
            let allUuidsArray = [];

            // Handle the new format where each category has audiobooks and display_type
            Object.entries(data).forEach(([categoryName, categoryData]) => {
               // Check if this is the new format with audiobooks and display_type
               if (
                  categoryData &&
                  typeof categoryData === "object" &&
                  "audiobooks" in categoryData
               ) {
                  processedCategories[categoryName] = {
                     audiobooks: categoryData.audiobooks || [],
                     displayType: categoryData.display_type || "default",
                  };
                  allUuidsArray = [
                     ...allUuidsArray,
                     ...categoryData.audiobooks,
                  ];
               } else if (Array.isArray(categoryData)) {
                  // Fallback for old format (directly array of UUIDs)
                  processedCategories[categoryName] = {
                     audiobooks: categoryData,
                     displayType: "default",
                  };
                  allUuidsArray = [...allUuidsArray, ...categoryData];
               }
            });

            setCategories(processedCategories);
            setAllUuids([...new Set(allUuidsArray)]); // Deduplicate

            // Check if there are any books
            if (allUuidsArray.length === 0) {
               setPublicItems([]);
               setInitialLoading(false);
               return;
            }

            // Load metadata for all books
            await loadAllBooks(allUuidsArray);
         } else if (Array.isArray(data)) {
            // Legacy fallback: just an array of UUIDs
            console.log(
               "Received data in old array format, converting to category format"
            );
            setAllUuids(data);
            setCategories({
               "All Audiobooks": {
                  audiobooks: data,
                  displayType: "default",
               },
            });

            if (data.length === 0) {
               setPublicItems([]);
               setInitialLoading(false);
               return;
            }

            // Load metadata for all books
            await loadAllBooks(data);
         } else {
            console.error("Unexpected data format:", data);
            setPublicItems([]);
            setInitialLoading(false);
         }
      } catch (error) {
         console.error("Error fetching public feed:", error);
         setPublicItems([]);
         setInitialLoading(false);
      }
   };

   // Load metadata for all books
   const loadAllBooks = async (uuids) => {
      try {
         setLoading(true);

         // Ensure we have unique UUIDs
         const uniqueUuids = [...new Set(uuids)];

         // Fetch metadata for all books at once
         const booksMetadata = await loadBooksMetadata(uniqueUuids);

         setPublicItems(booksMetadata);
      } catch (error) {
         console.error("Error loading all books:", error);
      } finally {
         setLoading(false);
         setInitialLoading(false);
      }
   };

   // Load metadata for a list of book UUIDs
   const loadBooksMetadata = async (uuids) => {
      try {
         // Fetch metadata for each book UUID
         const bookPromises = uuids.map((uuid) => fetchBookMetadata(uuid));
         const books = await Promise.all(bookPromises);
         return books;
      } catch (error) {
         console.error("Error loading books metadata:", error);
         return [];
      }
   };

   // Scroll category container left or right
   const scrollCategory = (categoryKey, direction) => {
      const container = scrollContainerRefs.current[categoryKey];
      if (!container) return;

      const scrollAmount = 600; // Adjust as needed
      const currentScroll = container.scrollLeft;

      if (direction === "left") {
         container.scrollTo({
            left: currentScroll - scrollAmount,
            behavior: "smooth",
         });
      } else {
         container.scrollTo({
            left: currentScroll + scrollAmount,
            behavior: "smooth",
         });
      }
   };

   // Initial load effect - fetch all audiobooks by categories
   useEffect(() => {
      fetchAudiobooks();
      return () => {
         setPublicItems([]);
         setAllUuids([]);
         setCategories({});
      };
   }, []);

   // Auto-play effect for handling pending audiobooks
   useEffect(() => {
      const shouldAutoPlay = sessionStorage.getItem("autoPlayAfterLogin");
      const pendingAudiobookStr = localStorage.getItem("pendingAudiobookData");

      console.log("Auto-play check:", {
         shouldAutoPlay,
         pendingAudiobook: pendingAudiobookStr ? "exists" : "none",
         publicItemsCount: publicItems.length,
         hasAudioContext: !!audioContext,
      });

      // Only try to auto-play if we have all the necessary pieces
      if (
         shouldAutoPlay &&
         pendingAudiobookStr &&
         publicItems.length > 0 &&
         audioContext
      ) {
         try {
            const pendingAudiobook = JSON.parse(pendingAudiobookStr);
            console.log(
               "Found pending audiobook:",
               pendingAudiobook.title || pendingAudiobook.uuid
            );

            // Wait for a short delay to ensure everything is loaded
            setTimeout(() => {
               // Find the book in the loaded public items
               const bookToPlay = publicItems.find(
                  (book) => book.uuid === pendingAudiobook.uuid
               );

               if (bookToPlay) {
                  console.log(
                     "Found matching book in public items, auto-playing:",
                     bookToPlay.title || bookToPlay.uuid
                  );

                  // Clear the flags and stored data ONLY after finding the book
                  localStorage.removeItem("pendingAudiobookData");
                  sessionStorage.removeItem("autoPlayAfterLogin");
                  delete window.pendingAudiobook;
                  delete window.loginReason;

                  // Play the book
                  audioContext.loadBook(bookToPlay, 1, true);
               } else {
                  console.warn(
                     "Book not found in public items. UUID:",
                     pendingAudiobook.uuid
                  );
                  console.log(
                     "Available books:",
                     publicItems.map((b) => ({ uuid: b.uuid, title: b.title }))
                  );
               }
            }, 1000); // Longer delay to ensure everything is loaded
         } catch (error) {
            console.error("Error processing pending audiobook:", error);
         }
      }
   }, [publicItems, audioContext]);

   // Handle opening book details
   const handleOpenDetail = (book) => {
      setSelectedBook(book);
   };

   // Handle closing book details
   const handleCloseDetail = () => {
      setSelectedBook(null);
   };

   // Handle when a book is selected for generation from the prompt selector
   const handleBookSelected = (book) => {
      console.log(
         `Book selected for generation from public feed: ${book.title} (${book.uuid})`
      );
   };

   // Set up scroll container ref for a category
   const setCategoryRef = (categoryKey, el) => {
      scrollContainerRefs.current[categoryKey] = el;
   };

   // Render different category display types
   const renderCategoryContent = (category, categoryData, isEven) => {
      // Extract display type and audiobooks
      const { displayType = "default", audiobooks = [] } = categoryData;

      // Filter for books that have metadata loaded
      const booksInCategory = audiobooks.filter(
         (uuid) => bookMetadataMap[uuid]
      );

      // Skip empty categories
      if (booksInCategory.length === 0) return null;

      // Default display type (horizontal scroll)
      if (displayType === "default") {
         return (
            <div
               key={category}
               style={{
                  marginBottom: "2.5rem",
                  width: "100%",
               }}
            >
               <h3 style={styles.categoryTitle}>
                  {String(category[0]).toUpperCase() +
                     String(category).slice(1)}
               </h3>

               {/* Content wrapper */}
               <div
                  style={{
                     position: "relative",
                     padding: "0 50px",
                  }}
               >
                  {/* Horizontal scrollable container */}
                  <div
                     ref={(el) => setCategoryRef(category, el)}
                     style={styles.categoryBooks}
                  >
                     {booksInCategory.map((uuid) => {
                        const book = bookMetadataMap[uuid];
                        return book ? (
                           <PublicFeedItem
                              key={uuid}
                              book={book}
                              onOpenDetail={handleOpenDetail}
                              theme={theme}
                           />
                        ) : null;
                     })}
                  </div>

                  {/* Navigation arrows - only show if enough books to scroll */}
                  {booksInCategory.length > 3 && (
                     <>
                        <button
                           style={{
                              ...styles.scrollArrow,
                              ...styles.leftArrow,
                           }}
                           onClick={() => scrollCategory(category, "left")}
                           aria-label="Scroll left"
                        >
                           &#10094;
                        </button>
                        <button
                           style={{
                              ...styles.scrollArrow,
                              ...styles.rightArrow,
                           }}
                           onClick={() => scrollCategory(category, "right")}
                           aria-label="Scroll right"
                        >
                           &#10095;
                        </button>
                     </>
                  )}
               </div>
            </div>
         );
      }

      // For future display types, add more conditional blocks here
      // For example:
      // if (displayType === 'grid') { ... }
      // if (displayType === 'featured') { ... }

      // Fallback to default display type if unknown type
      return null;
   };

   // Create a book metadata lookup map for categories
   const bookMetadataMap = {};
   publicItems.forEach((book) => {
      bookMetadataMap[book.uuid] = book;
   });

   return (
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "3rem" }}>
         {/* Header with title and create button */}
         {isAuthenticated && (
            <div>
               <AudiobookPromptSelector onBookSelected={handleBookSelected} />
            </div>
         )}
        

            <div
               style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "transparent",
               }}
            >
               <h2
                  className="inter-font"
                  style={{
                     color: "var(--text-primary)",
                     fontWeight: "700",
                     margin: "0 2rem 2rem 0",
                     paddingTop: "1rem",
                     background: "transparent",
                  }}
               >
                  Our community is creative, listen in...
               </h2>
            </div>

            {/* Loading indicator positioned below the title */}
            {initialLoading && (
               <div
                  style={{
                     display: "flex",
                     justifyContent: "center",
                     padding: "1rem",
                     marginBottom: "30px",
                  }}
               >
                  <p style={{ color: "var(--text-primary)" }}>
                     Loading public audiobooks...
                  </p>
               </div>
            )}

            {/* Prompt to login for unauthenticated users */}
            {!isAuthenticated && (
               <div
                  style={{
                     marginBottom: "30px",
                     padding: "20px",
                     backgroundColor: isLight ? "#e0f2fe" : "#0c4a6e", // Light blue/Dark blue
                     borderRadius: "0.5rem",
                     boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                     display: "flex",
                     justifyContent: "space-between",
                     alignItems: "center",
                  }}
               >
                  <div>
                     <h3
                        style={{
                           marginBottom: "5px",
                           color: isLight ? "#0369a1" : "#7dd3fc",
                        }}
                     >
                        Create Your Own Audiobooks
                     </h3>
                     <p
                        style={{
                           margin: 0,
                           color: isLight ? "#0c4a6e" : "#e0f2fe",
                        }}
                     >
                        Log in to create and generate your own custom
                        audiobooks!
                     </p>
                  </div>
                  <button
                     onClick={() => window.setView && window.setView("login")}
                     style={{
                        backgroundColor: isLight ? "#0284c7" : "#38bdf8",
                        color: isLight ? "white" : "#0c4a6e",
                        padding: "0.5rem 1rem",
                        borderRadius: "0.375rem",
                        fontSize: "0.9rem",
                        border: "none",
                        cursor: "pointer",
                     }}
                  >
                     Log In
                  </button>
               </div>
            )}

         {/* Book categories */}
         {Object.keys(categories).length > 0 ? (
            <div style={{ width: "100%" }}>
               {/* Render books by category */}
               {Object.entries(categories).map(
                  ([category, categoryData], index) => {
                     const isEven = index % 2 === 0;
                     return renderCategoryContent(
                        category,
                        categoryData,
                        isEven
                     );
                  }
               )}
            </div>
         ) : (
            !initialLoading && (
               <div className="content-container">
                  <div
                     style={{
                        backgroundColor: isLight
                           ? "var(--bg-secondary)"
                           : "rgba(34, 34, 34, 0.98)",
                        borderRadius: "0.5rem",
                        border: `1px solid ${
                           isLight ? "var(--border-color)" : "#333"
                        }`,
                        padding: "2rem",
                        textAlign: "center",
                        boxShadow: isLight
                           ? "0 4px 8px rgba(0, 0, 0, 0.1)"
                           : "0 4px 8px rgba(0, 0, 0, 0.3)",
                     }}
                  >
                     <div
                        style={{
                           width: "50px",
                           height: "50px",
                           borderRadius: "50%",
                           backgroundColor: isLight
                              ? "rgba(75, 85, 99, 0.1)"
                              : "rgba(75, 85, 99, 0.1)",
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                           margin: "0 auto 1rem auto",
                        }}
                     >
                        <span
                           style={{
                              color: isLight ? "#666" : "#999",
                              fontSize: "1.5rem",
                           }}
                        >
                           🎧
                        </span>
                     </div>
                     <p
                        style={{
                           color: "var(--text-primary)",
                           fontSize: "1.125rem",
                           fontWeight: "500",
                           marginBottom: "0.5rem",
                        }}
                     >
                        No public audiobooks available
                     </p>
                     <p
                        style={{
                           color: isLight ? "var(--text-muted)" : "#999",
                           fontSize: "0.875rem",
                        }}
                     >
                        Check back soon for community-created audiobooks
                     </p>
                  </div>
               </div>
            )
         )}
         {/* Loading indicator for more items */}
         {loading && publicItems.length > 0 && !initialLoading && (
            <div
               style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "1rem",
               }}
            >
               <p style={{ color: "var(--text-primary)" }}>
                  Loading audiobooks...
               </p>
            </div>
         )}
         {/* Detail modal */}
         {selectedBook && (
            <AudiobookDetailModal
               book={selectedBook}
               onClose={handleCloseDetail}
               theme={theme}
            />
         )}
      </div>
   );
}

// PublicFeedItem component with "now playing" status indication
const PublicFeedItem = ({ book, onOpenDetail, theme }) => {
   const [imageSrc, setImageSrc] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(false);
   const [isHovered, setIsHovered] = useState(false);
   const styles = getStyles(theme); // Get styles based on theme
   
   // Get audio context to check if this book is currently playing
   const audioContext = useContext(AudioContext);
   const isCurrentlyPlaying = audioContext?.currentBook?.uuid === book.uuid && audioContext?.playbackState?.isPlaying;


   useEffect(() => {
      let isMounted = true;

      const loadImage = async () => {
         try {
            // No CDS token needed for public books
            const userId = localStorage.getItem("userId") || "";
            const imageUrl = `${config.cdsUrl}/user/audiobooks/${book.uuid}/cover?size=256x256&user_id=${userId}`;

            const headers = {
               Accept: "image/jpeg,image/png,*/*",
               Origin: config.primaryUrl || window.location.origin,
            };

            const response = await fetch(imageUrl, {
               method: "GET",
               headers,
               mode: "cors",
            });

            if (!response.ok) {
               throw new Error(`Failed to load image: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            if (isMounted) {
               setImageSrc(objectUrl);
               setLoading(false);
            }
         } catch (err) {
            console.error("Error loading image:", err);
            if (isMounted) {
               setError(true);
               setLoading(false);
            }
         }
      };

      loadImage();

      return () => {
         isMounted = false;
         if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
         }
      };
   }, [book.uuid, imageSrc]);

   // Define playing indicator styles
   const playingIndicatorStyle = {
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme === "light" ? "rgba(16, 185, 129, 0.9)" : "rgba(16, 185, 129, 0.9)",
      color: "white",
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "0.6rem",
      fontWeight: "bold",
      display: "flex",
      alignItems: "center",
      zIndex: 5,
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
   };

   // Animated playing icon style
   const playingIconStyle = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "4px",
   };

   // Animation bars for the playing indicator
   const playingAnimationBars = () => (
      <div style={playingIconStyle}>
         <div style={{
            display: "flex",
            alignItems: "flex-end",
            height: "10px",
            gap: "1px"
         }}>
            {[1, 2, 3].map((bar) => (
               <div
                  key={bar}
                  style={{
                     width: "2px",
                     height: `${3 + bar * 2}px`,
                     backgroundColor: "white",
                     animation: `barAnim${bar} 0.8s infinite alternate ease-in-out`,
                  }}
               />
            ))}
         </div>
      </div>
   );
   
   // Add keyframes for the playing animation to the document if they don't exist
   useEffect(() => {
      if (!document.getElementById('playing-animation-keyframes')) {
         const styleSheet = document.createElement("style");
         styleSheet.id = 'playing-animation-keyframes';
         styleSheet.textContent = `
            @keyframes barAnim1 { 0% { height: 3px; } 100% { height: 7px; } }
            @keyframes barAnim2 { 0% { height: 5px; } 100% { height: 9px; } }
            @keyframes barAnim3 { 0% { height: 7px; } 100% { height: 5px; } }
         `;
         document.head.appendChild(styleSheet);
      }
   }, []);

   return (
      <div
         className="book-card"
         style={{
            ...styles.bookCard,
            ...(isHovered ? styles.bookCardHover : {}),
            // Add border highlight if currently playing
            ...(isCurrentlyPlaying ? {
               borderColor: theme === "light" ? "rgba(16, 185, 129, 0.8)" : "rgba(16, 185, 129, 0.8)",
               borderWidth: "2px",
               boxShadow: `0 0 15px rgba(16, 185, 129, ${theme === "light" ? "0.3" : "0.5"})`,
            } : {})
         }}
         onClick={() => onOpenDetail(book)}
         onMouseEnter={() => setIsHovered(true)}
         onMouseLeave={() => setIsHovered(false)}
      >
         {/* Image container with fixed height */}
         <div style={{
            ...styles.bookCardImage,
            position: "relative" // Ensure this is positioned relatively for absolute positioning
         }}>
            {loading ? (
               <div
                  style={{
                     height: "100%",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                  }}
               >
                  <span
                     style={{
                        color: theme === "light" ? "#9ca3af" : "#9ca3af",
                     }}
                  >
                     Loading...
                  </span>
               </div>
            ) : error ? (
               <div
                  style={{
                     height: "100%",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                  }}
               >
                  <span style={{ color: "#ef4444" }}>Error</span>
               </div>
            ) : (
               <img
                  src={imageSrc || "/api/placeholder/256/256"}
                  alt={`${book.title || "Audiobook"} cover`}
                  style={{
                     width: "100%",
                     height: "100%",
                     objectFit: "cover",
                  }}
               />
            )}
            
            {/* Now Playing Indicator */}
            {isCurrentlyPlaying && (
               <div style={playingIndicatorStyle}>
                  {playingAnimationBars()}
                  <span>Playing</span>
               </div>
            )}
         </div>

         {/* Content section - reduced spacing */}
         <div style={{ padding: "0.75rem" }}>
            {book.title && <h3 style={styles.bookCardTitle}>{book.title}</h3>}

            {/* Tags if available */}
            {book.tags && book.tags.length > 0 && (
               <div
                  style={{
                     marginBottom: "0.375rem",
                     overflow: "hidden",
                     height: "2rem",
                  }}
               >
                  {book.tags.map((tag, index) => (
                     <span key={index} style={styles.bookCardTag}>
                        {tag}
                     </span>
                  ))}
               </div>
            )}

            {/* Highlight/description if available */}
            {book.highlight && (
               <div>
                  <p style={styles.bookCardHighlight}>{book.highlight}</p>
                  <a
                     href="#"
                     style={styles.bookCardSeeMore}
                     onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenDetail(book);
                     }}
                  >
                     see more...
                  </a>
               </div>
            )}
         </div>
      </div>
   );
};

// Enhanced Modal component with playback status indication
const AudiobookDetailModal = ({ book, onClose, theme }) => {
   const [imageSrc, setImageSrc] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(false);
   const audioContext = useContext(AudioContext);
   const { isAuthenticated } = useUser(); // Get authentication status
   const isLight = theme === "light";
   
   // Check if this book is currently playing
   const isCurrentlyPlaying = audioContext?.currentBook?.uuid === book.uuid;
   const playbackState = audioContext?.playbackState || {};

   useEffect(() => {
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = "hidden";

      // Load the image at a larger resolution
      const loadImage = async () => {
         try {
            const userId = localStorage.getItem("userId") || "";
            // Request a larger image for the modal
            const imageUrl = `${config.cdsUrl}/user/audiobooks/${book.uuid}/cover?size=512x512&user_id=${userId}`;

            const headers = {
               Accept: "image/jpeg,image/png,*/*",
               Origin: config.primaryUrl || window.location.origin,
            };

            const response = await fetch(imageUrl, {
               method: "GET",
               headers,
               mode: "cors",
            });

            if (!response.ok) {
               throw new Error(`Failed to load image: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            setImageSrc(objectUrl);
            setLoading(false);
         } catch (err) {
            console.error("Error loading image:", err);
            setError(true);
            setLoading(false);
         }
      };

      loadImage();

      // Cleanup function
      return () => {
         document.body.style.overflow = "";
         if (imageSrc) {
            URL.revokeObjectURL(imageSrc);
         }
      };
   }, [book.uuid]);

   // Handle play button click with auth check
   const handlePlay = () => {
      if (!isAuthenticated) {
         console.log(
            "User not authenticated, saving book for later playback:",
            book.title || book.uuid
         );

         // Store the book object - both in window and localStorage
         window.pendingAudiobook = book;

         // Use more persistent storage with a specific key that won't get cleared
         localStorage.setItem("pendingAudiobookData", JSON.stringify(book));

         // Flag for auto-play
         sessionStorage.setItem("autoPlayAfterLogin", "true");

         // Reason for special login prompt
         window.loginReason = "audiobook_access";

         // Redirect to login page
         if (window.setView) {
            window.setView("login");
            onClose(); // Close the modal
         }
         return;
      }

      if (audioContext && audioContext.loadBook) {
         if (isCurrentlyPlaying && playbackState.isPlaying) {
            // If already playing this book, pause it
            audioContext.togglePlayPause();
         } else if (isCurrentlyPlaying && !playbackState.isPlaying) {
            // If this book is loaded but paused, resume playback
            audioContext.togglePlayPause();
         } else {
            // Start playing this book from the beginning or saved position
            const startChapter = book.current_chapter || 1;
            console.log(`PublicFeed: Playing book ${book.title} from chapter ${startChapter}`);
            audioContext.loadBook(book, startChapter, true);
         }
         // Modal remains open so user can see the playback status update
      }
   };

   // Animation bars for the playing indicator (smaller version)
   const playingAnimationBars = () => (
      <div style={{
         display: "inline-flex",
         alignItems: "center",
         justifyContent: "center",
         marginRight: "6px",
      }}>
         <div style={{
            display: "flex",
            alignItems: "flex-end",
            height: "14px",
            gap: "2px"
         }}>
            {[1, 2, 3].map((bar) => (
               <div
                  key={bar}
                  style={{
                     width: "3px",
                     height: `${4 + bar * 2}px`,
                     backgroundColor: "white",
                     animation: `barAnim${bar} 0.8s infinite alternate ease-in-out`,
                  }}
               />
            ))}
         </div>
      </div>
   );

   return (
      <div
         style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "1rem",
         }}
         onClick={(e) => {
            // Close the modal when clicking the background overlay
            if (e.target === e.currentTarget) {
               onClose();
            }
         }}
      >
         {/* Blurred background overlay */}
         <div
            style={{
               position: "fixed",
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               backgroundColor: isLight
                  ? "rgba(0, 0, 0, 0.2)"
                  : "rgba(0, 0, 0, 0.4)",
               backdropFilter: "blur(5px)",
               WebkitBackdropFilter: "blur(5px)", // For Safari support
               zIndex: 999,
            }}
         />

         {/* Modal container */}
         <div
            style={{
               position: "fixed",
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               display: "flex",
               alignItems: "center",
               justifyContent: "center",
               zIndex: 1000,
               padding: "1rem",
            }}
            onClick={(e) => {
               // Close the modal when clicking the background overlay
               if (e.target === e.currentTarget) {
                  onClose();
               }
            }}
         >
            {/* Modal content */}
            <div
               style={{
                  backgroundColor: isLight
                     ? "var(--bg-elevated)"
                     : "rgba(0, 0, 0, 0.9)",
                  color: "var(--text-primary)",
                  borderRadius: "1.5rem",
                  maxWidth: "1200px",
                  width: "100%",
                  height: "75vh",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  overflow: "hidden",
                  zIndex: 1001, // Make sure modal content is above the backdrop
                  border: isLight ? "1px solid var(--border-color)" : "none",
               }}
            >
               {/* Close button */}
               <button
                  onClick={onClose}
                  className="inter-font"
                  style={{
                     position: "absolute",
                     top: "1.5rem",
                     right: "1.5rem",
                     backgroundColor: isLight
                        ? "rgba(0, 0, 0, 0.1)"
                        : "rgb(197, 197, 197)",
                     color: isLight ? "var(--text-primary)" : "white",
                     border: "none",
                     borderRadius: "50%",
                     width: "3rem",
                     height: "3rem",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     cursor: "pointer",
                     zIndex: 10,
                     fontWeight: "700",
                     fontSize: "1.5rem",
                  }}
               >
                  ✕
               </button>

               {/* Content area with scrolling */}
               <div
                  style={{
                     display: "flex",
                     flexDirection: "column",
                     overflowY: "auto",
                     maxHeight: "80vh",
                  }}
               >
                  {/* Book details area */}
                  <div
                     style={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "wrap",
                     }}
                  >
                     {/* Left side - Image */}
                     <div
                        style={{
                           flex: "0 0 350px",
                           padding: "2rem",
                           position: "relative", // For positioning the playing indicator
                        }}
                     >
                        {loading ? (
                           <div
                              style={{
                                 width: "100%",
                                 paddingBottom: "100%",
                                 backgroundColor: isLight ? "#f3f4f6" : "#333",
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "center",
                                 position: "relative",
                              }}
                           >
                              <span
                                 style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    color: isLight ? "#9ca3af" : "#9ca3af",
                                 }}
                              >
                                 Loading...
                              </span>
                           </div>
                        ) : error ? (
                           <div
                              style={{
                                 width: "100%",
                                 paddingBottom: "100%",
                                 backgroundColor: isLight ? "#f3f4f6" : "#333",
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "center",
                                 position: "relative",
                              }}
                           >
                              <span
                                 style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    color: "#ef4444",
                                 }}
                              >
                                 Error
                              </span>
                           </div>
                        ) : (
                           <div style={{ position: "relative" }}>
                              <img
                                 src={imageSrc || "/api/placeholder/512/512"}
                                 alt={`${book.title || "Audiobook"} cover`}
                                 style={{
                                    width: "100%",
                                    height: "auto",
                                    borderRadius: "1.5rem",
                                    boxShadow: isLight
                                       ? "0 4px 6px rgba(0, 0, 0, 0.1)"
                                       : "0 4px 6px rgba(0, 0, 0, 0.3)",
                                 }}
                              />
                              
                              {/* Now playing indicator on the image */}
                              {isCurrentlyPlaying && (
                                 <div
                                    style={{
                                       position: "absolute",
                                       top: "20px",
                                       right: "20px",
                                       backgroundColor: "rgba(16, 185, 129, 0.9)",
                                       color: "white",
                                       padding: "6px 12px",
                                       borderRadius: "20px",
                                       fontSize: "0.75rem",
                                       fontWeight: "bold",
                                       display: "flex",
                                       alignItems: "center",
                                       boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                                    }}
                                 >
                                    {playingAnimationBars()}
                                    <span>Now Playing</span>
                                 </div>
                              )}
                           </div>
                        )}

                        {/* Play button - with dynamic text based on play state */}
                        <button
                           onClick={handlePlay}
                           style={{
                              backgroundColor: isCurrentlyPlaying && playbackState.isPlaying 
                                 ? "#e11d48" // red for pause
                                 : "#007AFF", // blue for play
                              color: "white",
                              border: "none",
                              borderRadius: "0.375rem",
                              padding: "0.75rem 1rem",
                              marginTop: "3rem",
                              width: "100%",
                              fontSize: "1rem",
                              fontWeight: "700",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                           }}
                        >
                           {isCurrentlyPlaying 
                              ? (playbackState.isPlaying ? "Pause" : "Resume") 
                              : "Listen"}
                        </button>
                     </div>

                     {/* Right side - Text content */}
                     <div
                        style={{
                           flex: "1 1 300px",
                           padding: "1.5rem",
                           paddingLeft: "0",
                        }}
                     >
                        {/* Title */}
                        {book.title && (
                           <h2
                              className="inter-font"
                              style={{
                                 fontSize: "1.75rem",
                                 fontWeight: "bold",
                                 marginBottom: "0.25rem",
                                 marginTop: "3rem",
                                 color: "var(--text-primary)",
                              }}
                           >
                              {book.title}
                           </h2>
                        )}

                        {/* Author */}
                        {book.author_name && (
                           <div
                              className="inter-font"
                              style={{
                                 fontSize: "1.125rem",
                                 color: isLight
                                    ? "var(--text-muted)"
                                    : "rgba(256,256,256,0.6)",
                                 marginBottom: "2rem",
                              }}
                           >
                              by <strong>{book.author_name}</strong>
                           </div>
                        )}

                        {/* Playback status if currently playing */}
                        {isCurrentlyPlaying && (
                           <div
                              style={{
                                 backgroundColor: isLight ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.2)",
                                 border: "1px solid rgba(16, 185, 129, 0.3)",
                                 borderRadius: "0.5rem",
                                 padding: "1rem",
                                 marginBottom: "1.5rem",
                                 color: isLight ? "#065f46" : "#10b981",
                              }}
                           >
                              <div style={{ 
                                 fontSize: "0.875rem", 
                                 display: "flex", 
                                 alignItems: "center"
                              }}>
                                 <span style={{ marginRight: "1rem" }}>
                                    {playbackState.isPlaying ? "Currently playing" : "Paused"}
                                 </span>
                                 
                                 <span>
                                    Chapter {audioContext.currentChapter || 1}
                                 </span>
                                 
                                 {audioContext.formatTime && (
                                    <span style={{ marginLeft: "auto" }}>
                                       {audioContext.formatTime(playbackState.currentTime)} / 
                                       {audioContext.formatTime(playbackState.duration)}
                                    </span>
                                 )}
                              </div>
                           </div>
                        )}

                        {/* Tags */}
                        {book.tags && book.tags.length > 0 && (
                           <div style={{ marginBottom: "1.25rem" }}>
                              {book.tags.map((tag, index) => (
                                 <span
                                    key={index}
                                    className="inter-font"
                                    style={{
                                       display: "inline-block",
                                       padding: "0.5rem 1rem",
                                       marginRight: "0.5rem",
                                       marginBottom: "0.5rem",
                                       borderRadius: "0.5rem",
                                       fontSize: "0.875rem",
                                       fontWeight: "700",
                                       backgroundColor: isLight
                                          ? "rgb(239, 239, 239)"
                                          : "#333",
                                       color: isLight ? "#000" : "#fff",
                                       border: isLight
                                          ? "1px solid #e5e7eb"
                                          : "1px solid #555",
                                    }}
                                 >
                                    {tag}
                                 </span>
                              ))}
                           </div>
                        )}

                        {/* Full description */}
                        {book.highlight && (
                           <div>
                              <p
                                 className="inter-font"
                                 style={{
                                    fontSize: "1.125rem",
                                    color: "var(--text-primary)",
                                    lineHeight: "1.5rem",
                                    whiteSpace: "pre-line", // Preserve line breaks in text
                                    fontWeight: "300",
                                    marginTop: "2rem",
                                    marginRight: "3rem",
                                 }}
                              >
                                 {book.highlight}
                              </p>
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   );
};
